import React from 'react';
import PropTypes from 'prop-types';

const ShoorahSchemesList = ({ schemes, onSelectScheme, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!schemes || schemes.length === 0) {
    return (
      <div className="text-center py-12">
        <svg
          className="w-16 h-16 text-gray-400 mx-auto mb-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
          />
        </svg>
        <p className="text-lg font-medium text-gray-900 dark:text-white">No schemes available</p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Please check back later</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-0">
      {schemes.map((scheme, index) => (
        <div key={index}>
          <div className="bg-white dark:bg-shoorah-darkBgTabColor p-6 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer">
            <div className="flex items-start gap-6">
              {/* Icon */}
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                  {scheme.imageUrl ? (
                    <img
                      src={scheme.imageUrl}
                      alt={scheme.title}
                      className="w-10 h-10 object-contain"
                    />
                  ) : (
                    <svg
                      className="w-10 h-10 text-purple-600 dark:text-purple-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                {/* Title */}
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {scheme.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{scheme.desc}</p>

                {/* Points */}
                {scheme.points && scheme.points.length > 0 && (
                  <ul className="space-y-2">
                    {scheme.points.map((point, pointIndex) => (
                      <li
                        key={pointIndex}
                        className="flex items-start text-sm text-gray-700 dark:text-gray-300"
                      >
                        <svg
                          className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Horizontal divider - show for all items except the last one */}
          {index < schemes.length - 1 && (
            <div className="border-b border-gray-200 dark:border-gray-600" />
          )}
        </div>
      ))}
    </div>
  );
};

ShoorahSchemesList.propTypes = {
  schemes: PropTypes.arrayOf(
    PropTypes.shape({
      imageUrl: PropTypes.string,
      title: PropTypes.string.isRequired,
      desc: PropTypes.string.isRequired,
      points: PropTypes.arrayOf(PropTypes.string)
    })
  ),
  onSelectScheme: PropTypes.func,
  loading: PropTypes.bool
};

ShoorahSchemesList.defaultProps = {
  schemes: [],
  onSelectScheme: null,
  loading: false
};

export default ShoorahSchemesList;
